import { useCallback, useEffect, useRef, useState } from "react";
import { Upload, Download, Trash2, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader } from "@/pages/app/AppLayout";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const CATS = { licence: "Licence", contract: "Contract", certificate: "Certificate / training", identification: "ID / right to work", tax_super: "Tax & super", other: "Other" };
const size = (b) => (b > 1048576 ? `${(b / 1048576).toFixed(1)} MB` : `${Math.ceil(b / 1024)} KB`);

const Expiry = ({ d }) => {
  if (!d.expiry_date) return <span className="text-slate-400">—</span>;
  const tone = d.days_to_expiry < 0 ? "bg-red-50 text-red-700" : d.days_to_expiry <= 30 ? "bg-amber-50 text-amber-800" : "bg-slate-100 text-slate-600";
  const text = d.days_to_expiry < 0 ? "Expired" : d.days_to_expiry <= 30 ? `${d.days_to_expiry} days left` : new Date(`${d.expiry_date}T00:00`).toLocaleDateString("en-AU");
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-semibold ${tone}`}>{text}</span>;
};

const UploadForm = ({ mgr, members, onDone }) => {
  const { membership } = useAuth();
  const ref = useRef(null);
  const [f, setF] = useState({ membership_id: membership.membership_id, title: "", category: "licence", expiry_date: "" });
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(f).forEach(([k, v]) => v && fd.append(k, v));
    setBusy(true);
    try {
      await api.post("/documents", fd);
      toast.success("Document uploaded");
      setF({ ...f, title: "", expiry_date: "" });
      setFile(null);
      ref.current.value = "";
      onDone();
    } catch (e) { toast.error(formatError(e)); } finally { setBusy(false); }
  };
  const field = (label, el) => <div className="space-y-1.5"><Label className="text-xs">{label}</Label>{el}</div>;
  return (
    <div className="mb-6 grid gap-3 rounded-2xl border border-slate-200 bg-white p-5 sm:grid-cols-2 lg:grid-cols-6 lg:items-end" data-testid="doc-upload-form">
      {mgr && field("Employee", <Select value={f.membership_id} onValueChange={(v) => setF({ ...f, membership_id: v })}><SelectTrigger className="rounded-xl" data-testid="doc-member-select"><SelectValue /></SelectTrigger><SelectContent>{members.map((m) => <SelectItem key={m.membership_id} value={m.membership_id}>{m.name}</SelectItem>)}</SelectContent></Select>)}
      {field("Title", <Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="e.g. RSA certificate" className="rounded-xl" data-testid="doc-title-input" />)}
      {field("Type", <Select value={f.category} onValueChange={(v) => setF({ ...f, category: v })}><SelectTrigger className="rounded-xl" data-testid="doc-category-select"><SelectValue /></SelectTrigger><SelectContent>{Object.entries(CATS).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}</SelectContent></Select>)}
      {field("Expiry date (optional)", <Input type="date" value={f.expiry_date} onChange={(e) => setF({ ...f, expiry_date: e.target.value })} className="rounded-xl" data-testid="doc-expiry-input" />)}
      {field("File (PDF, Word or photo, max 10 MB)", <Input ref={ref} type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.webp,.heic" onChange={(e) => setFile(e.target.files[0] || null)} className="rounded-xl" data-testid="doc-file-input" />)}
      <Button onClick={submit} disabled={!file || !f.title.trim() || busy} className="rounded-xl bg-[#00C16B] hover:bg-[#00A85C]" data-testid="doc-upload-btn"><Upload className="mr-1 h-4 w-4" />{busy ? "Uploading…" : "Upload"}</Button>
    </div>
  );
};

export default function DocumentsPage() {
  const { membership, user } = useAuth();
  const mgr = membership.role !== "employee";
  const [docs, setDocs] = useState([]);
  const [members, setMembers] = useState([]);
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => api.get("/documents", { params: filter === "all" ? {} : { membership_id: filter } }).then((r) => setDocs(r.data)).catch((e) => toast.error(formatError(e))), [filter]);
  useEffect(() => {
    load();
  }, [load]);
  useEffect(() => {
    if (mgr) { api.get("/members").then((r) => setMembers(r.data.filter((m) => m.status === "active"))); }
  }, [mgr]);
  const get = async (d) => {
    try {
      const res = await api.get(`/documents/${d.document_id}/download`, { responseType: "blob" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(res.data);
      a.download = d.filename;
      a.click();
    } catch (e) { toast.error(formatError(e)); }
  };
  const remove = async (d) => {
    if (!window.confirm(`Remove "${d.title}"?`)) return;
    try { await api.delete(`/documents/${d.document_id}`); toast.success("Document removed"); load(); } catch (e) { toast.error(formatError(e)); }
  };
  return (
    <div data-testid="documents-page">
      <PageHeader eyebrow="Documents" title={mgr ? "Staff documents" : "My documents"}>
        {mgr && <Select value={filter} onValueChange={setFilter}><SelectTrigger className="w-52 rounded-xl" data-testid="doc-filter-select"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">Everyone</SelectItem>{members.map((m) => <SelectItem key={m.membership_id} value={m.membership_id}>{m.name}</SelectItem>)}</SelectContent></Select>}
      </PageHeader>
      <UploadForm mgr={mgr} members={members} onDone={load} />
      <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
        {docs.length === 0 ? <p className="p-8 text-center text-sm text-slate-500" data-testid="docs-empty">No documents yet.</p> : (
          <table className="w-full min-w-[720px] text-sm" data-testid="docs-table">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500"><tr>{[...(mgr ? ["Employee"] : []), "Document", "Type", "Expiry", "Uploaded", ""].map((h) => <th key={h} className="px-4 py-3">{h}</th>)}</tr></thead>
            <tbody>{docs.map((d) => (
              <tr key={d.document_id} className="border-t border-slate-100" data-testid={`doc-row-${d.document_id}`}>
                {mgr && <td className="px-4 py-3 font-medium">{d.employee_name}</td>}
                <td className="px-4 py-3"><p className="flex items-center gap-2 font-medium"><FileText className="h-4 w-4 text-slate-400" />{d.title}</p><p className="text-xs text-slate-500">{d.filename} · {size(d.size)}</p></td>
                <td className="px-4 py-3 text-xs">{CATS[d.category]}</td>
                <td className="px-4 py-3"><Expiry d={d} /></td>
                <td className="px-4 py-3 text-xs text-slate-500">{new Date(d.created_at).toLocaleDateString("en-AU")} · {d.uploaded_by_name}</td>
                <td className="px-4 py-3"><div className="flex justify-end gap-1">
                  <Button size="icon" variant="ghost" onClick={() => get(d)} aria-label="Download" data-testid={`doc-download-${d.document_id}`}><Download className="h-4 w-4" /></Button>
                  {(mgr || d.uploaded_by === user.user_id) && <Button size="icon" variant="ghost" onClick={() => remove(d)} aria-label="Remove" data-testid={`doc-delete-${d.document_id}`}><Trash2 className="h-4 w-4 text-red-600" /></Button>}
                </div></td>
              </tr>))}</tbody>
          </table>)}
      </div>
    </div>
  );
}
